import classNames from 'classnames';
import _ from 'lodash';
import React, { useContext } from 'react';

import DraggableHOC from './../../ricos-common-plugins/draggable/draggable-HOC';
import ResizerHOC from './../../ricos-common-plugins/resizable/resizer';
import { RicosContext } from './../../ricos-context/RicosContext';
import type { ButtonData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import type { PluginProps } from './../../ricos-types';
import { isNodeSelected } from './../../_shared/is-node-selected';
import {
  getNodeWidth,
  getPluginContainerClassNames,
  getPluginFocusClassNames,
  getPluginPlacementClassNames,
} from './../../_shared/node-styles';
import { OverlayHoc } from './../../tiptap-extensions/overlay';

import styles from '../statics/styles/default-styles-scss';
import { ButtonViewer } from '../viewer/button-viewer';

const createStylesHOC = (extraClassName?: string) => (Component: React.FC<PluginProps>) => {
  return (props: PluginProps) => {
    const { isMobile } = useContext(RicosContext);
    const isTextWrapEnabled = props.extension.config.isTextWrap ?? true;

    const className = classNames(
      getPluginContainerClassNames(isMobile),
      getPluginPlacementClassNames(props.node, isTextWrapEnabled),
      extraClassName
    );

    const width = getNodeWidth(props.node, isMobile);

    const style: React.CSSProperties = {
      width: width ? `min(100%, ${width}px)` : undefined,
    };

    return (
      <div className={className} style={style}>
        <Component {...props} />
      </div>
    );
  };
};

const ButtonResizerHOC = (Component: React.FC<PluginProps>) => {
  const ResizableComponent = ResizerHOC(Component);

  return (props: PluginProps) => {
    return props.extension.config.disableResize ? (
      <Component {...props} />
    ) : (
      <ResizableComponent {...props} />
    );
  };
};

const SelectionHOC = (Component: React.FC<PluginProps>) => {
  return (props: PluginProps) => {
    const isFocused = isNodeSelected(props.editor.state, props.node.attrs.id);
    const className = getPluginFocusClassNames(isFocused);

    return (
      <div className={className}>
        <Component {...props} />
      </div>
    );
  };
};

export const Button: React.FC<PluginProps> = _.flowRight(
  // We lift button margin to the plugin container so that resizer and selection are not affected
  createStylesHOC(styles.button_margin),
  ButtonResizerHOC,
  DraggableHOC,
  SelectionHOC,
  OverlayHoc
)(({ componentData, settings, node }: PluginProps) => {
  return (
    <ButtonViewer
      nodeId={node.attrs.id}
      buttonData={componentData as ButtonData}
      settings={settings}
    />
  );
});
